import {
  OpportunityAddress,
  OpportunityDTO,
  OpportunityFinancialDetails,
  PropertyCondition,
  TernaryOption,
  UpdateOpportunityRequest,
} from '@inversor-hub/types';
import { cleanFormValues } from '@/utils/misc.ts';
import { OpportunityFormData } from '@/features/opportunities/form/types.ts';

export const toOpportunityFormData = (opportunity: OpportunityDTO): OpportunityFormData => {
  return {
    title: opportunity.title ?? '',
    description: opportunity.description ?? '',
    type: opportunity.type ?? 'TRADITIONAL_RENT',
    address: opportunity.addressInfo?.address ?? '',
    postalCode: opportunity.addressInfo?.postalCode ?? '',
    city: opportunity.addressInfo?.city ?? '',
    province: opportunity.addressInfo?.province ?? '',
    locationDescription: opportunity.addressInfo?.locationDescription ?? '',
    showApproximateAddress: opportunity.addressInfo?.showApproximateAddress ?? false,
    cadastralReference: opportunity.cadastralReference ?? '',
    observations: opportunity.observations ?? '',
    sellers: opportunity.sellers ?? '',
    itePassed: opportunity.itePassed?.value ?? '',
    itePassedObservations: opportunity.itePassed?.observations ?? '',
    buildingYear: opportunity.buildingYear ? String(opportunity.buildingYear) : '',
    houseOrientation: opportunity.houseOrientation ?? '',
    buildingFloor: opportunity.buildingFloor ?? '',
    rooms: opportunity.rooms ?? 0,
    bathrooms: opportunity.bathrooms ?? 0,
    squareMeters: opportunity.squareMeters ?? 0,
    electricityCondition: opportunity.propertyCondition?.electricityCondition ?? 0,
    plumbingCondition: opportunity.propertyCondition?.plumbingCondition ?? 0,
    streetCondition: opportunity.propertyCondition?.streetCondition ?? 0,
    facadeCondition: opportunity.propertyCondition?.facadeCondition ?? 0,
    shuttersCondition: opportunity.propertyCondition?.shuttersCondition ?? null,
    stairCondition: opportunity.propertyCondition?.stairCondition ?? null,
    terraceCondition: opportunity.propertyCondition?.terraceCondition ?? null,
    electricityRegistration: opportunity.electricityRegistration?.value ?? '',
    electricityRegistrationObservations: opportunity.electricityRegistration?.observations ?? '',
    waterRegistration: opportunity.waterRegistration?.value ?? '',
    waterRegistrationObservations: opportunity.waterRegistration?.observations ?? '',
    vacantSale: opportunity.vacantSale?.value ?? '',
    vacantSaleObservations: opportunity.vacantSale?.observations ?? '',
    hasElevator: opportunity.hasElevator?.value ?? '',
    hasElevatorObservations: opportunity.hasElevator?.observations ?? '',
    energyCertificate: opportunity.energyCertificate?.value ?? '',
    energyCertificateObservations: opportunity.energyCertificate?.observations ?? '',
    debtCertificate: opportunity.debtCertificate?.value ?? '',
    debtCertificateObservations: opportunity.debtCertificate?.observations ?? '',
    simpleCopy: opportunity.simpleCopy?.value ?? '',
    simpleCopyObservations: opportunity.simpleCopy?.observations ?? '',
    vpo: opportunity.vpo?.value ?? '',
    vpoObservations: opportunity.vpo?.observations ?? '',
    inheritance: opportunity.inheritance?.value ?? '',
    inheritanceObservations: opportunity.inheritance?.observations ?? '',
    communityFee: opportunity.financialDetails?.communityFee ?? null,
    propertyTaxIbi: opportunity.financialDetails?.propertyTaxIbi ?? null,
    totalCommunityExpenses: opportunity.financialDetails?.totalCommunityExpenses ?? null,
    wasteTax: opportunity.financialDetails?.wasteTax ?? null,
    nonNegotiatedPrice: opportunity.financialDetails?.nonNegotiatedPrice ?? null,
    negotiatedPrice: opportunity.financialDetails?.negotiatedPrice ?? null,
    purchaseTaxItp: opportunity.financialDetails?.purchaseTaxItp ?? null,
    purchaseExpenses: opportunity.financialDetails?.purchaseExpenses ?? null,
    realtorCommission: opportunity.financialDetails?.realtorCommission ?? null,
    psiCommission: opportunity.financialDetails?.psiCommission ?? null,
    expectedMonthlyRent: opportunity.financialDetails?.expectedMonthlyRent ?? null,
    roomRent: opportunity.financialDetails?.roomRent ?? null,
    recurringIncome: opportunity.financialDetails?.recurringIncome ?? null,
    deedAmount: opportunity.financialDetails?.deedAmount ?? null,
    referenceValue: opportunity.financialDetails?.referenceValue ?? null,
    rentDefaultInsurance: opportunity.financialDetails?.rentDefaultInsurance ?? null,
    homeInsurance: opportunity.financialDetails?.homeInsurance ?? null,
    otherExpenses: opportunity.financialDetails?.otherExpenses ?? null,
    notaryExpenses: opportunity.financialDetails?.notaryExpenses ?? null,
    registryExpenses: opportunity.financialDetails?.registryExpenses ?? null,
    renovationCost: opportunity.financialDetails?.renovationCost ?? null,
    estimatedSalePrice: opportunity.financialDetails?.estimatedSalePrice ?? null,
    grossReturnRental: opportunity.grossReturnRental ?? null,
    grossReturnRooms: opportunity.grossReturnRooms ?? null,
    netReturnRooms: opportunity.netReturnRooms ?? null,
    netReturnRental: opportunity.netReturnRental ?? null,
    grossReturnFlipping: opportunity.grossReturnFlipping ?? null,
    netReturnFlipping: opportunity.netReturnFlipping ?? null,
    isPrivate: opportunity.isPrivate,
    investorId: opportunity.investorId ?? '',
  };
};

export const toOpportunityUpdate = (formData: OpportunityFormData): UpdateOpportunityRequest => {
  const addressInfo: OpportunityAddress = {
    address: formData.address,
    postalCode: formData.postalCode,
    city: formData.city,
    province: formData.province,
    locationDescription: formData.locationDescription,
    showApproximateAddress: formData.showApproximateAddress,
  };

  const financialDetails: OpportunityFinancialDetails = {
    communityFee: formData.communityFee,
    propertyTaxIbi: formData.propertyTaxIbi,
    totalCommunityExpenses: formData.totalCommunityExpenses,
    wasteTax: formData.wasteTax,
    nonNegotiatedPrice: formData.nonNegotiatedPrice,
    negotiatedPrice: formData.negotiatedPrice,
    purchaseTaxItp: formData.purchaseTaxItp,
    purchaseExpenses: formData.purchaseExpenses,
    realtorCommission: formData.realtorCommission,
    psiCommission: formData.psiCommission,
    expectedMonthlyRent: formData.expectedMonthlyRent,
    roomRent: formData.roomRent,
    recurringIncome: formData.recurringIncome,
    deedAmount: formData.deedAmount,
    referenceValue: formData.referenceValue,
    rentDefaultInsurance: formData.rentDefaultInsurance,
    homeInsurance: formData.homeInsurance,
    otherExpenses: formData.otherExpenses,
    notaryExpenses: formData.notaryExpenses,
    registryExpenses: formData.registryExpenses,
    renovationCost: formData.renovationCost,
    estimatedSalePrice: formData.estimatedSalePrice,
  };

  const propertyCondition: PropertyCondition = {
    electricityCondition: formData.electricityCondition,
    plumbingCondition: formData.plumbingCondition,
    streetCondition: formData.streetCondition,
    facadeCondition: formData.facadeCondition,
    shuttersCondition: formData.shuttersCondition,
    stairCondition: formData.stairCondition,
    terraceCondition: formData.terraceCondition,
  };

  const {
    grossMonthlyIncomeTraditional,
    grossReturnTraditional,
    netMonthlyIncomeTraditional,
    netMonthlyIncomeRooms,
    netReturnRooms,
    netReturnTraditional,
    grossReturnRooms,
    grossMonthlyIncomeRooms,
    grossReturnFlipping,
    netReturnFlipping,
  } = calculateTransactionResults(formData);

  return cleanFormValues({
    title: formData.title,
    description: formData.description,
    type: formData.type,
    addressInfo,
    isPrivate: formData.isPrivate,
    cadastralReference: formData.cadastralReference,
    observations: formData.observations,
    sellers: formData.sellers,
    buildingYear: Number(formData.buildingYear),
    houseOrientation: formData.houseOrientation,
    buildingFloor: formData.buildingFloor,
    rooms: formData.rooms,
    bathrooms: formData.bathrooms,
    squareMeters: formData.squareMeters,
    propertyCondition,
    itePassed: formData.itePassed
      ? {
          value: formData.itePassed as TernaryOption,
          observations: formData.itePassedObservations,
        }
      : undefined,
    electricityRegistration: formData.electricityRegistration
      ? {
          value: formData.electricityRegistration as TernaryOption,
          observations: formData.electricityRegistrationObservations,
        }
      : undefined,
    waterRegistration: formData.waterRegistration
      ? {
          value: formData.waterRegistration as TernaryOption,
          observations: formData.waterRegistrationObservations,
        }
      : undefined,
    vacantSale: formData.vacantSale
      ? {
          value: formData.vacantSale as TernaryOption,
          observations: formData.vacantSaleObservations,
        }
      : undefined,
    hasElevator: formData.hasElevator
      ? {
          value: formData.hasElevator as TernaryOption,
          observations: formData.hasElevatorObservations,
        }
      : undefined,
    energyCertificate: formData.energyCertificate
      ? {
          value: formData.energyCertificate as TernaryOption,
          observations: formData.energyCertificateObservations,
        }
      : undefined,
    debtCertificate: formData.debtCertificate
      ? {
          value: formData.debtCertificate as TernaryOption,
          observations: formData.debtCertificateObservations,
        }
      : undefined,
    simpleCopy: formData.simpleCopy
      ? {
          value: formData.simpleCopy as TernaryOption,
          observations: formData.simpleCopyObservations,
        }
      : undefined,
    vpo: formData.vpo
      ? {
          value: formData.vpo as TernaryOption,
          observations: formData.vpoObservations,
        }
      : undefined,
    inheritance: formData.inheritance
      ? {
          value: formData.inheritance as TernaryOption,
          observations: formData.inheritanceObservations,
        }
      : undefined,
    financialDetails,
    grossMonthlyIncomeTraditional,
    grossReturnRental: grossReturnTraditional,
    grossMonthlyIncomeRental: grossMonthlyIncomeTraditional,
    netMonthlyIncomeRooms,
    netReturnRooms,
    netReturnRental: netReturnTraditional,
    netMonthlyIncomeRental: netMonthlyIncomeTraditional,
    grossReturnRooms,
    grossMonthlyIncomeRooms,
    grossReturnFlipping,
    netReturnFlipping,
    investorId: formData.investorId,
  });
};

export interface FinancialResults {
  grossMonthlyIncomeTraditional: number;
  grossMonthlyIncomeRooms: number;
  netMonthlyIncomeTraditional: number;
  netMonthlyIncomeRooms: number;
  grossReturnTraditional: number;
  grossReturnRooms: number;
  netReturnTraditional: number;
  netReturnRooms: number;
  grossReturnFlipping: number;
  netReturnFlipping: number;
  totalExpenses: number;
}

export function calculateTransactionResults(formData: OpportunityFormData): FinancialResults {
  const totalExpenses =
    (formData.negotiatedPrice ?? 0) +
    (formData.purchaseTaxItp ?? 0) +
    (formData.notaryExpenses ?? 0) +
    (formData.registryExpenses ?? 0) +
    (formData.renovationCost ?? 0) +
    (formData.purchaseExpenses ?? 0) +
    (formData.psiCommission ?? 0) +
    (formData.realtorCommission ?? 0);
  const grossAnnualIncomeTraditional = (formData.expectedMonthlyRent ?? 0) * 12;
  const grossAnnualIncomeRooms = (formData.roomRent ?? 0) * formData.rooms * 12;

  const grossMonthlyIncomeTraditional = grossAnnualIncomeTraditional / 12;
  const grossMonthlyIncomeRooms = grossAnnualIncomeRooms / 12;

  const annualExpenses =
    (formData.communityFee ?? 0) * 12 +
    (formData.propertyTaxIbi ?? 0) +
    (formData.wasteTax ?? 0) +
    (formData.homeInsurance ?? 0) +
    (formData.rentDefaultInsurance ?? 0) +
    (formData.otherExpenses ?? 0);

  const netAnnualIncomeTraditional = grossAnnualIncomeTraditional - annualExpenses;
  const netAnnualIncomeRooms = grossAnnualIncomeRooms - annualExpenses;

  const netMonthlyIncomeTraditional = netAnnualIncomeTraditional / 12;
  const netMonthlyIncomeRooms = netAnnualIncomeRooms / 12;

  const grossReturnTraditional =
    totalExpenses !== 0 ? grossAnnualIncomeTraditional / totalExpenses : 0;

  const grossReturnRooms = totalExpenses !== 0 ? grossAnnualIncomeRooms / totalExpenses : 0;

  const netReturnTraditional = totalExpenses !== 0 ? netAnnualIncomeTraditional / totalExpenses : 0;

  const netReturnRooms = totalExpenses !== 0 ? netAnnualIncomeRooms / totalExpenses : 0;

  // FLIPPING: Gross Return = (Sale Price - Renovation - Buy Price) / (Renovation + Buy Price)
  const flippingBuyPrice = formData.negotiatedPrice ?? 0;
  const flippingRenovation = formData.renovationCost ?? 0;
  const flippingSalePrice = formData.estimatedSalePrice ?? 0;
  const flippingBaseCost = flippingBuyPrice + flippingRenovation;

  const grossProfitFlipping = flippingSalePrice - flippingBuyPrice - flippingRenovation;
  const grossReturnFlipping = flippingBaseCost !== 0 ? grossProfitFlipping / flippingBaseCost : 0;

  // Net return includes all transaction costs
  const netProfitFlipping = flippingSalePrice - totalExpenses;
  const netReturnFlipping = totalExpenses !== 0 ? netProfitFlipping / totalExpenses : 0;

  return {
    grossMonthlyIncomeTraditional,
    grossMonthlyIncomeRooms,
    netMonthlyIncomeTraditional,
    netMonthlyIncomeRooms,
    grossReturnTraditional,
    grossReturnRooms,
    netReturnTraditional,
    netReturnRooms,
    grossReturnFlipping,
    netReturnFlipping,
    totalExpenses,
  };
}
